import React from 'react'
import { Box, Typography } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import AdxInput from 'components/custom/AdxInput'
import AdxDateRangePicker from 'components/reusable/AdxDateRangePicker'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { format, parse } from 'date-fns'

// API services
import { useGetDisabledDate } from 'services/api/modules/leave/calendar'
import { useGetEmployeeEntitlement } from 'services/api/modules/leave/entitlement'

const FillCreditLeave = ({
  values,
  handleChange,
  setFieldValue,
  reasonRequired,
  setFieldTouched,
  errors,
  touched,
  validateField,
}) => {
  const { data: disabledCalendarData } = useGetDisabledDate()
  const { data: creditPolicy } = useGetEmployeeEntitlement()

  const takenLeave = disabledCalendarData?.data?.leave?.length
    ? disabledCalendarData?.data?.leave?.map((day) =>
        format(parse(day.date, `yyyy-MM-dd`, new Date()), `dd MMMM yyyy`)
      )
    : []

  const leaveCreditPolicy = creditPolicy?.leave_credit[0]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, px: 3, py: 2 }}>
      <Box sx={{ gap: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Shift and select date form */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AdxDateRangePicker
            disabledDates={disabledCalendarData && [...takenLeave]}
            startCycle={new Date(leaveCreditPolicy?.cycle_start_date)}
            endCycle={new Date(leaveCreditPolicy?.cycle_end_date)}
            values={{
              startDate: values.startDate,
              endDate: values.endDate,
              duration: values.duration,
            }}
            setValue={(e) => {
              setFieldValue('startDate', e.startDate)
              setFieldValue('endDate', e.endDate)
              setFieldValue('duration', e.duration)
            }}
            label="Date"
            sx={{ width: 1 }}
            errorMessage={<ErrorMessage name="startDate" component={AdxErrorMessage} />}
            setFieldTouched={setFieldTouched}
            errors={errors}
            isErrorShown={errors.startDate && errors.endDate && errors.duration}
            touched={touched}
            validateField={validateField}
          />
        </Box>

        {/* Days Requested Box */}
        <Box
          sx={{
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            backgroundColor: 'gray-background.main',
            borderRadius: '4.5px',
            display: 'flex',
            p: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: null, sm: 'space-between' },
          }}
        >
          <Typography variant="body1" color="light-gray-text.main">
            Leave days requested
          </Typography>
          <Typography variant="body1" color="dark-gray-text.main">
            {values.duration} {values.duration > 1 ? 'Days' : 'Day'}
          </Typography>
        </Box>
      </Box>

      {/* Multine input for reasons / remarks */}
      <Field
        as={AdxInput}
        label={`Leave purpose ${reasonRequired ? `(Required)` : `(Optional)`}`}
        multiline
        minRows={2}
        name="reason"
        value={values.reason}
        onChange={handleChange}
        placeholder="Enter reason here..."
        charLimit={150}
        sxInput={{ p: 2 }}
        errorMessage={<ErrorMessage name="reason" component={AdxErrorMessage} />}
        isErrorShown={errors.reason && touched.reason}
      />
      <Box
        sx={{
          backgroundColor: 'gray-background.main',
          rowGap: 1.5,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '4.5px',
          p: 2,
        }}
      >
        <Typography
          variant="body1"
          color="dark-gray-text.main"
          sx={{ textDecoration: 'underline' }}
        >
          Leave Credit Request Process:
        </Typography>
        <Box>
          <Typography
            variant="body2"
            color="white.main"
            sx={{
              bgcolor: 'alternate.main',
              width: 'fit-content',
              px: 1,
              py: 0.5,
              borderRadius: '4.5px',
              mb: 1,
            }}
          >
            Step 1
          </Typography>
          <Typography variant="body1" color="dark-gray-text.main">
            For Replacement Leave, select the day(s) worked.
            <br />
            For other leaves without entitlement, select the day(s) off you are requesting.
            <br />
            Submit and await approval.
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="body2"
            color="white.main"
            sx={{
              bgcolor: 'alternate.main',
              width: 'fit-content',
              px: 1,
              py: 0.5,
              borderRadius: '4.5px',
              mb: 1,
            }}
          >
            Step 2
          </Typography>
          <Typography variant="body1" color="dark-gray-text.main">
            Upon approval, upload evidence of your activity.
            <br />
            Submit and await approval
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="body2"
            color="white.main"
            sx={{
              bgcolor: 'alternate.main',
              width: 'fit-content',
              px: 1,
              py: 0.5,
              borderRadius: '4.5px',
              mb: 1,
            }}
          >
            Step 3
          </Typography>
          <Typography variant="body1" color="dark-gray-text.main">
            Once approved, Replacement Credit is credited to you.
            <br />
            Apply for leave using Replacement Credit in 'My Request'.
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default FillCreditLeave
